/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';
import { jsx, cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import Skeleton from '@atlaskit/skeleton';
import { Card } from '@atlaskit/smart-card/card/lazy';
import { FooterBlock } from '@atlaskit/smart-card/flexible/footer-block';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import { token } from '@atlaskit/tokens';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------

const i18n = defineMessages({
	sourceLabel: {
		id: 'suggestions-panel.source-label',
		defaultMessage: 'Source',
		description: 'Label for the sources section on an AI suggestion item',
	},
});

// ---------------------------------------------------------------------------
// Smart card ui options — strip the card's own chrome so it blends in
// ---------------------------------------------------------------------------

const cardUiOptions = {
	hidePadding: true,
	hideElevation: true,
	hideBackground: true,
	clickableContainer: true,
} as const;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = cssMap({
	sourcesContainer: {
		overflow: 'hidden',
		width: '100%',
	},
	/**
	 * Always-visible card border — reserves layout space (minHeight: 58px)
	 * immediately so there is no layout shift when the skeleton transitions
	 * to the resolved smart card.
	 */
	sourceCard: {
		width: '100%',
		minHeight: '58px',
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxSizing: 'border-box',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		gap: token('space.075'),
		marginTop: token('space.100'),
	},
	/**
	 * Extra interactive styles applied only once the URL is resolved so the
	 * card looks clickable.
	 */
	sourceCardInteractive: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	/**
	 * The smart card is mounted immediately (so its IntersectionObserver fires
	 * and resolution begins) but kept invisible until onResolve fires.
	 */
	cardLoading: {
		visibility: 'hidden',
	},
});

// ---------------------------------------------------------------------------
// Skeleton shown while the smart card is resolving
// ---------------------------------------------------------------------------

function SourceSkeleton(): React.JSX.Element {
	return (
		<React.Fragment>
			<Skeleton width="60%" height="9px" />
			<Skeleton width="40%" height="9px" />
		</React.Fragment>
	);
}

// ---------------------------------------------------------------------------
// SourceCard — renders the smart card with a skeleton overlay while loading
// ---------------------------------------------------------------------------

function SourceCard({ url }: { url: string }): React.JSX.Element | null {
	const [status, setStatus] = React.useState<'loading' | 'resolved' | 'errored'>('loading');
	const handleResolve = React.useCallback(() => setStatus('resolved'), []);
	const handleError = React.useCallback(() => setStatus('errored'), []);

	if (status === 'errored') {
		return null;
	}

	return (
		<React.Fragment>
			{/* Card is mounted hidden so its IntersectionObserver fires immediately;
			    it becomes visible once onResolve fires. */}
			<div css={[status === 'loading' && styles.cardLoading]}>
				<Card
					appearance="block"
					ui={cardUiOptions}
					url={url}
					onResolve={handleResolve}
					onError={handleError}
				>
					<TitleBlock maxLines={1} hideIcon anchorTarget="_blank" />
					<FooterBlock />
				</Card>
			</div>
			{/* Skeleton sits alongside the hidden card while status === 'loading' */}
			{status === 'loading' && <SourceSkeleton />}
		</React.Fragment>
	);
}

export type SuggestionItemSourcesProps = {
	sources: NonNullable<Suggestion['sources']>;
};

function SuggestionItemSourcesInner({
	sources,
}: SuggestionItemSourcesProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();

	// We only show the first source for now (multiple sources aren't supported yet).
	// The backend supplies the URL, so a source without one can't be linked to and is
	// dropped — even when it has an ARI.
	const url = sources[0]?.url;

	if (!url) {
		return null;
	}

	return (
		<div css={styles.sourcesContainer}>
			<Inline space="space.050" alignBlock="center">
				<TeamworkGraphIcon label="" size="small" color={token('color.icon.subtle')} />
				<Text as="span" color="color.text.subtle" size="small">
					{formatMessage(i18n.sourceLabel)}
				</Text>
			</Inline>
			<div css={[styles.sourceCard, styles.sourceCardInteractive]}>
				<SourceCard key={url} url={url} />
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Public export — wrapped in TransparentErrorBoundary so that unexpected
// errors (e.g. missing SmartCardProvider context in tests) render nothing
// rather than crashing the whole suggestions panel.
// ---------------------------------------------------------------------------

export function SuggestionItemSources({
	sources,
}: SuggestionItemSourcesProps): React.JSX.Element | null {
	if (!sources.length) {
		return null;
	}

	return (
		<TransparentErrorBoundary attribution="suggestions-panel">
			<SuggestionItemSourcesInner sources={sources} />
		</TransparentErrorBoundary>
	);
}
